import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Lock, Plus } from "lucide-react";
import { Icon, Modal, Sheet, Spinner } from "@cntrl/ui";
import { isNative } from "@cntrl/native";
import type { components } from "@cntrl/api-client";
import { useBandContext } from "../../app/bandContext.js";
import { useOpsCalendarMyPay, type OpsCalendarItemKind } from "../../app/hooks/useShows.js";
import {
  useCloseOut,
  useMarkCollect,
  useMarkExpense,
  useMarkMemberPaid,
  useReopenScope,
  useShowTotalBreakdown,
} from "../../app/hooks/useMoney.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { RoleLabel } from "../contacts/RoleLabel.js";
import { initials } from "../../shells/mobile/accountRoles.js";
import { completedLine, expenseLines, money2, useShowSettleBox } from "./lib/calendarMoney.js";
import { venmoDeepLink, venmoNote, venmoWebLink } from "./lib/venmo.js";
import styles from "./CalendarEventDetails.module.scss";

type Detail = components["schemas"]["OpsCalendarItemDetail"];
type SettleBox = components["schemas"]["OutstandingSettleBox"];
type ScopeShow = components["schemas"]["OutstandingScopeShow"];
type MemberLine = components["schemas"]["OutstandingMemberLine"];
type ExpenseItem = components["schemas"]["OutstandingExpenseItem"];
type CollectItem = components["schemas"]["OutstandingCollectItem"];

// ART-305 v3 — the event sheet after a show has been played (handoff "Post Event States"). Two audiences:
//   · everyone on the show who is NOT on the money team gets "Your pay" (their own pay, from its own read);
//   · the money team (the seats that hold the settlement) gets a summary of the settlement and Outstanding Payments,
//     and the Money pop-up — tiles, settlements, collect, pay out and Close out — over the SAME Outstanding Payments
//     settle box the Booking page uses, so the two can never disagree.
// "Completed" is the show's Outstanding Payments scope being closed out: per show, or per run / tour for a show on one.

function shortDate(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const SCOPE_WORD: Record<string, string> = { show: "show", run: "run", tour: "tour" };

/** Re-read the open event (its Completed flag and Add expense) after a close-out or a reopen. */
function useInvalidateCalendarItem() {
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return () => void qc.invalidateQueries({ queryKey: ["band", bandId, "ops-calendar"] });
}

// ─── Non-money view: Your pay (1a–1c) ─────────────────────────────────────────────────────────────────────────

const PAY_PILL: Record<string, { label: string; tone: string }> = {
  unpaid: { label: "Unpaid", tone: "owed" },
  partial: { label: "Partly paid", tone: "owed" },
  paid: { label: "Paid", tone: "paid" },
};

/** "Your pay" — the amount, its status, the breakdown and who paid it. Paid status comes only from Outstanding
 *  Payments. Renders nothing when there is no pay stored for the viewer on this show. */
export function YourPayCard({ kind, id }: { kind: OpsCalendarItemKind; id: string }) {
  const q = useOpsCalendarMyPay(kind, id, true);
  const pay = q.data;
  if (!pay?.status) return null;

  const total = pay.totalCents ?? 0;
  const paid = pay.paidCents ?? 0;
  const owed = Math.max(0, total - paid);
  const lines = pay.lines ?? [];
  const payments = pay.payments ?? [];
  const lastPaid = payments.length ? payments[payments.length - 1]?.paidOn : null;
  const pill = PAY_PILL[pay.status] ?? PAY_PILL.unpaid!;

  const amount = pay.status === "partial" ? owed : total;
  const sub = pay.status === "paid"
    ? `Paid in full${lastPaid ? ` · ${shortDate(lastPaid)}` : ""}`
    : pay.status === "partial"
      ? `Still owed · ${money2(paid)} paid${lastPaid ? ` ${shortDate(lastPaid)}` : ""}`
      : "Still owed to you";
  // "Oct 12 · $100.00 via Venmo from Dana Brooks"
  const history = payments
    .map((p) => `${shortDate(p.paidOn)} · ${money2(p.amountCents)}`
      + (p.method ? ` via ${METHOD[p.method] ?? p.method}` : "")
      + (p.paidByName ? ` from ${p.paidByName}` : ""))
    .join(" · ");

  return (
    <section className={styles.card} aria-label="Your pay">
      <div className={styles.cardTop}>
        <div className={styles.cardHead}>
          <span className={styles.eyebrow}>Your pay</span>
          <span className={styles.statusPill} data-tone={pill.tone}>{pill.label}</span>
        </div>
        <p className={styles.bigAmount}>{money2(amount)}</p>
        <p className={styles.cardSub}>{sub}</p>
        {pay.status === "partial" && total > 0 && <Progress value={paid / total} />}
      </div>
      {lines.length > 1 && (
        <ul className={styles.cardRows}>
          {lines.map((l, i) => (
            <li key={`${l.kind}-${i}`} className={styles.cardRow}>
              <span className={styles.ledgerLabel}>
                {l.label}
                {l.sub && <span className={styles.ledgerSub}>{l.sub}</span>}
              </span>
              {pay.status === "partial" && (
                <span className={styles.rowStatus} data-tone={l.paid ? "paid" : "owed"}>{l.paid ? "Paid" : "Owed"}</span>
              )}
              <span className={styles.ledgerValue}>{money2(l.amountCents)}</span>
            </li>
          ))}
        </ul>
      )}
      <p className={styles.cardFoot}>{history || "Updates when Outstanding Payments marks you paid."}</p>
    </section>
  );
}

const METHOD: Record<string, string> = { venmo: "Venmo", cash: "cash", other: "other" };

function Progress({ value }: { value: number }) {
  return (
    <div className={styles.progress} role="progressbar" aria-valuemin={0} aria-valuemax={100}
      aria-valuenow={Math.round(Math.min(1, Math.max(0, value)) * 100)}>
      <span style={{ width: `${Math.min(100, Math.max(0, value * 100))}%` }} />
    </div>
  );
}

/** The locked footer once the show's payments are closed out (1c). */
export function ExpensesClosedBar() {
  return (
    <div className={styles.lockedBar}>
      <Icon icon={Lock} size={16} />
      <span>Show completed · expenses closed</span>
    </div>
  );
}

// ─── Money view: the summary on the details (1d / 1f) ────────────────────────────────────────────────────────

/** Settlement (net + SETTLED / OPEN) and Outstanding payments (N of M paid · $X owed) for a played show. */
export function MoneySummaryCard({ detail, box }: { detail: Detail; box: SettleBox | undefined }) {
  if (!box) return <div className={styles.loadingInline}><Spinner size={16} /></div>;
  const show = (box.shows ?? []).find((s) => s.showId === detail.showId);
  const settled = show?.settlementStatus === "settled";
  // "Outstanding payments" is what the band pays OUT (people + repaid expenses) — not the venue money it collects, which
  // would read as owed by the band. Close out still waits for both.
  const payOut = [
    ...(box.members ?? []).map((m) => ({ paid: !!m.paidOn, cents: m.amountCents ?? 0 })),
    ...(box.expenses ?? []).map((e) => ({ paid: !!e.paidOn, cents: e.amountCents ?? 0 })),
  ];
  const total = payOut.length;
  const done = payOut.filter((p) => p.paid).length;
  const owedCents = payOut.filter((p) => !p.paid).reduce((s, p) => s + p.cents, 0);
  const allPaid = done === total;
  return (
    <section className={styles.card} aria-label="Money">
      <div className={styles.cardRow}>
        <span className={styles.ledgerLabel}>
          Settlement
          <span className={styles.ledgerSub}>Net to artist {money2(show?.netCents)}</span>
        </span>
        <span className={styles.statusPill} data-tone={settled ? "paid" : "owed"}>{settled ? "Settled" : "Open"}</span>
      </div>
      <div className={styles.cardRow} data-stack="">
        <div className={styles.rowSpread}>
          <span className={styles.ledgerLabel}>
            Outstanding payments
            <span className={styles.ledgerSub}>
              {total === 0 ? "Nobody to pay" : `${done} of ${total} paid${owedCents > 0 ? `, ${money2(owedCents)} owed` : ""}`}
            </span>
          </span>
          <span className={styles.statusPill} data-tone={allPaid ? "paid" : "owed"}>{allPaid ? "Paid" : "Open"}</span>
        </div>
        {total > 0 && <Progress value={done / total} />}
      </div>
    </section>
  );
}

/** Reopen the show's payment scope (it goes back to played, expenses open again). */
export function ReopenScopeButton({ detail }: { detail: Detail }) {
  const reopen = useReopenScope();
  const refresh = useInvalidateCalendarItem();
  const kind = detail.closeoutScopeKind ?? "show";
  return (
    <button type="button" className={styles.btnGhost} disabled={reopen.isPending}
      onClick={() => detail.closeoutScopeId && reopen.mutate(
        { scopeKind: kind, scopeId: detail.closeoutScopeId }, { onSuccess: refresh })}>
      Reopen {SCOPE_WORD[kind] ?? "show"}
    </button>
  );
}

// ─── Money pop-up (1e / 1j / 1k) ─────────────────────────────────────────────────────────────────────────────

type PayTarget = { kind: "member"; line: MemberLine } | { kind: "expense"; item: ExpenseItem };

/**
 * The Money view: the four tiles (Terms · Merch · Expenses · Net to artist — the settlement's own figures), then the
 * settlement(s), Collect and Pay out, over the show's payment scope. A show on a run or tour opens on the GROUP, with a
 * switch to "This show" (its tiles, settlement and expenses, no payments — those are paid out with the group).
 */
export function MoneyView({ detail, isMobile, onOpenSettlement, onAddExpense, footer }: {
  detail: Detail;
  isMobile: boolean;
  /** Open one show's settlement (this show, or another show on the run). */
  onOpenSettlement: (show: ScopeShow) => void;
  onAddExpense: (() => void) | null;
  footer: (node: ReactNode) => ReactNode;
}) {
  const q = useShowSettleBox(detail, true);
  const box = q.data;
  const grouped = detail.closeoutScopeKind === "run" || detail.closeoutScopeKind === "tour";
  const [scope, setScope] = useState<"group" | "show">("group");
  const [paying, setPaying] = useState<PayTarget | null>(null);
  const closeOut = useCloseOut();
  const refresh = useInvalidateCalendarItem();

  if (q.isLoading) return <div className={styles.loading} role="status" aria-live="polite"><Spinner /></div>;
  if (q.isError || !box) {
    return (
      <div className={styles.scroll}>
        <div className={styles.subHead}><h2 className={styles.title}>Money</h2></div>
        <p className={styles.error}>Couldn&apos;t load the money for this show.</p>
      </div>
    );
  }

  const shows = box.shows ?? [];
  const thisShow = shows.find((s) => s.showId === detail.showId);
  const showMode = grouped && scope === "show";
  const kindWord = SCOPE_WORD[box.scopeKind ?? "show"] ?? "show";
  const groupName = box.title ?? "Run";
  const sub = showMode || !grouped
    ? [detail.venueName || detail.title, shortDate(detail.date)].filter(Boolean).join(" · ")
    : [groupName, `${shows.length} ${shows.length === 1 ? "show" : "shows"}`].join(" · ");

  const tiles = showMode
    ? { deal: thisShow?.dealCents, merch: thisShow?.merchCents, expenses: thisShow?.expensesCents, net: thisShow?.netCents }
    : { deal: box.dealCents, merch: box.merchCents, expenses: box.expensesCents, net: box.netCents };

  const members = box.members ?? [];
  const reimbursements = box.expenses ?? [];
  const payOut: PayTarget[] = [
    ...members.map((line): PayTarget => ({ kind: "member", line })),
    ...reimbursements.map((item): PayTarget => ({ kind: "expense", item })),
  ];
  const unpaid = payOut.filter((t) => !isPaid(t));
  const paidCount = payOut.length - unpaid.length;
  const owed = unpaid.reduce((s, t) => s + amountOf(t), 0);
  const collect = box.collect ?? [];
  const collectDue = collect.filter((c) => !c.receivedOn).reduce((s, c) => s + (c.amountCents ?? 0), 0);

  const closed = !!box.closedOut;
  const scopeKind = box.scopeKind ?? "show";
  const scopeId = box.scopeId ?? "";

  return (
    <>
      <div className={styles.scroll}>
        <div className={styles.subHead}>
          <h2 className={styles.title}>Money</h2>
          <p className={styles.date}>{sub}</p>
        </div>
        <div className={styles.form}>
          {grouped && (
            <div className={styles.segmented} role="tablist" aria-label="Show or group">
              <button type="button" role="tab" aria-selected={scope === "show"} onClick={() => setScope("show")}>
                This show
              </button>
              <button type="button" role="tab" aria-selected={scope === "group"} onClick={() => setScope("group")}>
                {groupName} · {shows.length}
              </button>
            </div>
          )}

          <div className={styles.tiles}>
            <Tile label="Terms" value={money2(tiles.deal)} />
            <Tile label="Merch" value={money2(tiles.merch)} />
            <Tile label="Expenses" value={`−${money2(tiles.expenses)}`} tone="negative" />
            <Tile label="Net to artist" value={money2(tiles.net)} tone="ink" />
          </div>

          {(showMode || !grouped) && thisShow && (
            <SettlementRow show={thisShow} label="Settlement" onOpen={() => onOpenSettlement(thisShow)} />
          )}

          {!showMode && grouped && (
            <>
              <SectionHead label="Settlements"
                aside={`${shows.filter((s) => s.settlementStatus === "settled").length} of ${shows.length} settled`} />
              <div className={styles.ledger}>
                {shows.map((s) => (
                  <SettlementRow key={s.showId} show={s} bare onOpen={() => onOpenSettlement(s)}
                    label={`${shortDate(s.date)} · ${s.venue}`} subOverride={s.city ?? undefined} />
                ))}
              </div>
            </>
          )}

          {showMode ? (
            <>
              <ThisShowExpenses showId={detail.showId ?? undefined} />
              <div className={styles.noteRow}>
                <span>Payments for this show are paid out with the {groupName}.</span>
                <button type="button" className={styles.inlineLink} onClick={() => setScope("group")}>
                  View {kindWord}
                </button>
              </div>
            </>
          ) : (
            <>
              {collect.length > 0 && (
                <>
                  <SectionHead label="Collect" aside={collectDue > 0 ? `${money2(collectDue)} due` : "All received"} />
                  <div className={styles.ledger}>
                    {collect.map((c) => <CollectRow key={`${c.showId}-${c.kind}`} item={c} locked={closed} />)}
                  </div>
                </>
              )}
              {payOut.length > 0 && (
                <>
                  <SectionHead label={grouped ? "Pay out" : "Outstanding payments"}
                    aside={`${paidCount} of ${payOut.length} paid${owed > 0 ? `, ${money2(owed)} owed` : ""}`}
                    warn={owed > 0} />
                  <div className={styles.ledger}>
                    {[...unpaid, ...payOut.filter(isPaid)].map((t) => (
                      <PayRow key={keyOf(t)} target={t} onPay={closed ? null : () => setPaying(t)} />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {showMode
        ? footer(onAddExpense && !closed ? (
          <div className={styles.buttons}>
            <button type="button" className={styles.btnGhost} onClick={onAddExpense}>
              <Icon icon={Plus} size={16} /> Add expense
            </button>
          </div>
        ) : null)
        : footer(closed ? (
          <p className={styles.helper}>{completedLine(box)}</p>
        ) : (
          <>
            <div className={styles.buttons}>
              <button type="button" className={styles.btnPrimary}
                disabled={!box.canCloseOut || closeOut.isPending}
                onClick={() => closeOut.mutate({ scopeKind, scopeId }, { onSuccess: refresh })}>
                Close out {kindWord}
              </button>
            </div>
            {!box.canCloseOut && <p className={styles.helperCenter}>Available once everything&apos;s settled</p>}
          </>
        ))}

      {paying && (
        <PayPanel target={paying} box={box} detail={detail} isMobile={isMobile} onClose={() => setPaying(null)} />
      )}
    </>
  );
}

function isPaid(t: PayTarget): boolean {
  return t.kind === "member" ? !!t.line.paidOn : !!t.item.paidOn;
}
function amountOf(t: PayTarget): number {
  return t.kind === "member" ? t.line.amountCents ?? 0 : t.item.amountCents ?? 0;
}
function keyOf(t: PayTarget): string {
  return t.kind === "member" ? `m:${t.line.personId}` : `e:${t.item.showId}:${t.item.key}`;
}

function Tile({ label, value, tone }: { label: string; value: string; tone?: "negative" | "ink" }) {
  return (
    <div className={styles.tile} data-tone={tone}>
      <span className={styles.tileLabel}>{label}</span>
      <span className={styles.tileValue}>{value}</span>
    </div>
  );
}

function SectionHead({ label, aside, warn }: { label: string; aside?: string; warn?: boolean }) {
  return (
    <div className={styles.formLabelRow}>
      <p className={styles.formLabel}>{label}</p>
      {aside && <span className={styles.sectionAside} data-warn={warn || undefined}>{aside}</span>}
    </div>
  );
}

function SettlementRow({ show, label, subOverride, bare, onOpen }: {
  show: ScopeShow; label: string; subOverride?: string; bare?: boolean; onOpen: () => void;
}) {
  const settled = show.settlementStatus === "settled";
  const sub = subOverride ?? (settled
    ? ["Settled", show.settledByName ? `by ${show.settledByName}` : null].filter(Boolean).join(" ")
      + (show.settledOn ? ` · ${shortDate(show.settledOn)}` : "")
    : `Net to artist ${money2(show.netCents)}`);
  return (
    <button type="button" className={bare ? styles.ledgerButton : `${styles.ledgerButton} ${styles.cardButton}`}
      onClick={onOpen}>
      <span className={styles.ledgerLabel}>
        {label}
        <span className={styles.ledgerSub}>{sub}</span>
      </span>
      <span className={styles.statusPill} data-tone={settled ? "paid" : "owed"}>{settled ? "Settled" : "Open"}</span>
      <Icon icon={ChevronRight} size={16} />
    </button>
  );
}

function ThisShowExpenses({ showId }: { showId: string | undefined }) {
  const q = useShowTotalBreakdown(showId, !!showId);
  const lines = expenseLines(q.data?.expenses);
  if (q.isLoading) return <div className={styles.loadingInline}><Spinner size={16} /></div>;
  if (lines.length === 0) return null;
  const total = lines.reduce((s, l) => s + (l.amountCents ?? 0), 0);
  return (
    <>
      <SectionHead label="This show's expenses" aside={`−${money2(total)}`} />
      <div className={styles.ledger}>
        {lines.map((l, i) => (
          <div key={`${l.label}-${i}`} className={styles.ledgerRow}>
            <span className={styles.ledgerLabel}>
              {l.label ?? "Expense"}
              {l.addedByName && (
                <span className={styles.ledgerSub}>{l.addedByName}{l.receiptMediaId ? " · receipt" : ""}</span>
              )}
            </span>
            <span className={styles.ledgerValue} data-negative="">−{money2(l.amountCents)}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function CollectRow({ item, locked }: { item: CollectItem; locked: boolean }) {
  const mark = useMarkCollect();
  const received = !!item.receivedOn;
  return (
    <div className={styles.ledgerRow}>
      <span className={styles.ledgerLabel}>
        {item.kind === "deposit" ? "Deposit" : "Balance"}
        <span className={styles.ledgerSub}>
          {item.venue} · {shortDate(item.date)}{item.kind === "deposit" ? "" : " · at show"}
        </span>
      </span>
      <span className={styles.ledgerValue} data-strong="">{money2(item.amountCents)}</span>
      {received ? (
        <span className={styles.rowStatus} data-tone="paid">Received</span>
      ) : !locked ? (
        <button type="button" className={styles.smallGhost} disabled={mark.isPending}
          onClick={() => mark.mutate({ showId: item.showId ?? "", kind: item.kind ?? "", received: true })}>
          Mark received
        </button>
      ) : null}
    </div>
  );
}

function PayRow({ target, onPay }: { target: PayTarget; onPay: (() => void) | null }) {
  const paid = isPaid(target);
  const amount = amountOf(target);
  const isMember = target.kind === "member";
  const personId = isMember ? target.line.personId : target.item.addedByPersonId;
  const name = isMember ? target.line.name ?? "Crew member" : target.item.addedByName ?? target.item.label ?? "Expense";
  return (
    <div className={styles.ledgerRow}>
      {isMember || personId ? (
        <PersonAvatar personId={personId ?? undefined} avatarMediaId={isMember ? target.line.avatarMediaId : undefined}
          initials={initials(name)} size="sm" />
      ) : null}
      <span className={styles.ledgerLabel}>
        {name}
        <span className={styles.ledgerSub}>
          {isMember
            ? <RoleLabel role={target.line.role ?? undefined} titleKey={target.line.titleKey ?? undefined} />
            : `Repay · ${target.item.label ?? "Expense"}`}
        </span>
      </span>
      <span className={styles.ledgerValue} data-strong="">{money2(amount)}</span>
      {paid ? (
        <span className={styles.rowStatus} data-tone="paid">Paid</span>
      ) : onPay ? (
        <button type="button" className={styles.smallPrimary} onClick={onPay}>Pay</button>
      ) : null}
    </div>
  );
}

// ─── Pay (1g–1i) ──────────────────────────────────────────────────────────────────────────────────────────────

/**
 * Pay one person: their breakdown, then Pay with Venmo (when they have a handle) and Mark paid. Venmo sends no status
 * back, so after the hand-off the panel asks "Did the Venmo payment go through?" — it stays open, so that is what the
 * person sees when they come back from the Venmo app. Our "paid" mark is the only record.
 */
function PayPanel({ target, box, detail, isMobile, onClose }: {
  target: PayTarget; box: SettleBox; detail: Detail; isMobile: boolean; onClose: () => void;
}) {
  const payMember = useMarkMemberPaid();
  const payExpense = useMarkExpense();
  const [askConfirm, setAskConfirm] = useState(false);
  const isMember = target.kind === "member";
  const name = isMember ? target.line.name ?? "Crew member" : target.item.addedByName ?? "Expense";
  const first = name.split(" ")[0] ?? name;
  const personId = isMember ? target.line.personId : target.item.addedByPersonId;
  const venmo = isMember ? target.line.venmoUsername : target.item.venmoUsername;
  const amount = amountOf(target);
  const where = box.scopeKind === "show"
    ? [detail.venueName || detail.title, shortDate(detail.date)].filter(Boolean).join(" · ")
    : box.title ?? "";
  const busy = payMember.isPending || payExpense.isPending;

  const rows: { label: string; cents: number }[] = isMember
    ? [
      { label: "Show pay", cents: target.line.showPayCents ?? 0 },
      { label: "Day pay", cents: target.line.payCents ?? 0 },
      { label: "Per diem", cents: target.line.perDiemCents ?? 0 },
    ].filter((r) => r.cents > 0)
    : [{ label: target.item.label ?? "Expense", cents: target.item.amountCents ?? 0 }];

  function markPaid(method: "venmo" | "cash") {
    const done = { onSuccess: onClose };
    if (target.kind === "member") {
      payMember.mutate({ scopeKind: box.scopeKind ?? "show", scopeId: box.scopeId ?? "", personId: target.line.personId ?? "", method }, done);
    } else {
      payExpense.mutate({ showId: target.item.showId ?? "", expenseKey: target.item.key ?? "", paid: true }, done);
    }
  }

  function openVenmo() {
    if (!venmo) return;
    const payment = { username: venmo, amountCents: amount, note: venmoNote(box.title ?? "Show") };
    if (isNative()) window.location.assign(venmoDeepLink(payment));
    else window.open(venmoWebLink(payment), "_blank", "noopener,noreferrer");
    setAskConfirm(true);
  }

  const body = (
    <div className={styles.payPanel}>
      <div className={styles.payHead}>
        <PersonAvatar personId={personId ?? undefined} avatarMediaId={isMember ? target.line.avatarMediaId : undefined}
          initials={initials(name)} size="md" />
        <div className={styles.payWho}>
          <h2 className={styles.payName}>Pay {name}</h2>
          <p className={styles.ledgerSub}>
            {isMember ? (
              <><RoleLabel role={target.line.role ?? undefined} titleKey={target.line.titleKey ?? undefined} />
                {where ? ` · ${where}` : ""}</>
            ) : `Repay an expense${where ? ` · ${where}` : ""}`}
          </p>
        </div>
        <span className={styles.payAmount}>{money2(amount)}</span>
      </div>
      <div className={styles.ledger}>
        {rows.map((r) => (
          <div key={r.label} className={styles.ledgerRow}>
            <span className={styles.ledgerLabel}>{r.label}</span>
            <span className={styles.ledgerValue}>{money2(r.cents)}</span>
          </div>
        ))}
      </div>
      {askConfirm ? (
        <>
          <p className={styles.payQuestion}>Did the Venmo payment go through?</p>
          <button type="button" className={styles.btnPrimary} disabled={busy} onClick={() => markPaid("venmo")}>
            Yes, mark paid
          </button>
          <button type="button" className={styles.btnGhost} onClick={onClose}>Not yet</button>
        </>
      ) : (
        <>
          {venmo && (
            <button type="button" className={styles.btnVenmo} onClick={openVenmo}>
              Pay with Venmo <span className={styles.venmoHandle}>· @{venmo}</span>
            </button>
          )}
          <button type="button" className={venmo ? styles.btnGhost : styles.btnPrimary} disabled={busy}
            onClick={() => markPaid("cash")}>
            Mark paid
          </button>
          <p className={styles.helperCenter}>
            {venmo
              ? "Opens Venmo with the amount and note filled in."
              : `${first} hasn't added a Venmo to their account. Pay another way, then mark paid.`}
          </p>
        </>
      )}
    </div>
  );

  return isMobile ? (
    <Sheet title={`Pay ${name}`} onClose={onClose} stacked>{body}</Sheet>
  ) : (
    <Modal title={`Pay ${name}`} hideHeader size="sm" onClose={onClose}>{body}</Modal>
  );
}
